import { Slider } from '../../r2/Slider';
import { ColorSwatch } from '../../r2/ColorSwatch';
import { useDesigner } from '../../context/DesignerContext';
import { SetTokenCommand } from '@bq/designer-core';
import type { ResolvedToken } from '@bq/designer-core';
import styles from './TokenSlider.module.css';

export interface TokenSliderProps {
  token: ResolvedToken;
  className?: string;
}

/**
 * Context-aware token editor (R3).
 *
 * Renders either a color swatch (for color tokens) or a numeric slider
 * (for size/number tokens). Dispatches SetTokenCommand through the
 * DesignerContext on value change.
 */
export function TokenSlider({ token, className }: TokenSliderProps) {
  const { document, execute, registry } = useDesigner();

  const currentValue = registry.resolveValue(
    token.path.cssVar,
    document.overrides,
  );
  const displayValue = currentValue ?? token.value;
  const isOverridden = document.overrides.has(token.path.cssVar);

  if (token.type === 'color') {
    return (
      <div
        data-crd-id="r3.bq-designer-token-slider"
        data-crd-layer="r3"
        className={`${styles.wrapper} ${isOverridden ? styles.overridden : ''} ${className ?? ''}`}
      >
        <ColorSwatch
          label={token.path.cssVar}
          rgb={displayValue}
          onChange={(rgb) => {
            execute(
              new SetTokenCommand(token.path, rgb, token.value),
            );
          }}
        />
      </div>
    );
  }

  const numericValue = parseFloat(displayValue) || 0;
  const { min, max, step, unit } = inferSliderRange(token);

  return (
    <div
      data-crd-id="r3.bq-designer-token-slider"
      data-crd-layer="r3"
      className={`${styles.wrapper} ${isOverridden ? styles.overridden : ''} ${className ?? ''}`}
    >
      <Slider
        label={token.path.cssVar}
        value={numericValue}
        min={min}
        max={max}
        step={step}
        unit={unit}
        onChange={(val) => {
          execute(
            new SetTokenCommand(token.path, String(val), token.value),
          );
        }}
      />
    </div>
  );
}

function inferSliderRange(token: ResolvedToken): {
  min: number;
  max: number;
  step: number;
  unit: string;
} {
  switch (token.type) {
    case 'number':
      return { min: 0, max: 10, step: 0.1, unit: '' };
    case 'size':
      if (token.value.endsWith('rem')) return { min: 0, max: 20, step: 0.25, unit: 'rem' };
      if (token.value.endsWith('px')) return { min: 0, max: 100, step: 1, unit: 'px' };
      if (token.value.endsWith('em')) return { min: 0, max: 10, step: 0.1, unit: 'em' };
      return { min: 0, max: 100, step: 1, unit: '' };
    default:
      return { min: 0, max: 100, step: 1, unit: '' };
  }
}
